/*
 * Dictation, through the platform's own recogniser.
 *
 * The Web Speech API is the only speech-to-text a browser offers without a
 * server of our own, and it is enough for the front door: one sentence, in the
 * customer's own words, that the FAQ matcher will read. Where it exists it is
 * the OS's recogniser — Google's on Android Chrome, Apple's in Safari — which
 * means the languages a customer's phone already understands are the languages
 * this understands, and nothing is recorded by us.
 *
 * WHERE IT DOES NOT EXIST the microphone is simply not offered, and that is
 * the whole contract of this file: `canListen()` is asked first, and `listen()`
 * returns null rather than throwing. Today that means Firefox, and — more
 * importantly — the Capacitor shell: Android's WebView and iOS's WKWebView both
 * ship without the API. A native build that wants the mic needs
 * @capacitor-community/speech-recognition behind this same interface; the
 * front door should not have to know which it got.
 *
 * `SpeechRecognition` is not in TypeScript's DOM library (only its result
 * types are), so the shape used here is written out. It is the subset this
 * app touches and nothing more.
 */

import { voiceLog } from './voicelog'

interface RecognitionAlternativeLike {
  transcript: string
}

interface RecognitionResultLike {
  isFinal: boolean
  length: number
  [index: number]: RecognitionAlternativeLike
}

interface RecognitionEventLike {
  results: ArrayLike<RecognitionResultLike>
}

interface RecognitionErrorLike {
  error: string
}

interface Recognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  onresult: ((event: RecognitionEventLike) => void) | null
  onerror: ((event: RecognitionErrorLike) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

type RecognitionConstructor = new () => Recognition

function constructor(): RecognitionConstructor | null {
  const scope = window as Window & {
    SpeechRecognition?: RecognitionConstructor
    webkitSpeechRecognition?: RecognitionConstructor
  }
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null
}

/** True where a microphone button would do something. */
export function canListen(): boolean {
  return typeof window !== 'undefined' && constructor() !== null
}

/**
 * Why a listen ended without a sentence, in terms of what to tell the customer.
 *
 *   refused      the microphone permission was denied. Say so, and point at
 *                the keyboard — the button will keep failing the same way.
 *   silent       nothing was said before the recogniser gave up. Not worth a
 *                message; the customer knows they said nothing.
 *   unavailable  the recogniser itself failed — no network for the cloud half,
 *                no audio device, an internal error. Worth one line, once.
 */
export type ListenFailure = 'refused' | 'silent' | 'unavailable'

export interface ListenHandlers {
  /** The sentence so far, including words the recogniser may still revise. */
  onHeard(text: string): void
  /** The sentence the recogniser has committed to. Fired at most once. */
  onFinal(text: string): void
  /**
   * Always fires last, exactly once, whether or not anything was heard.
   * `detail` is the recogniser's own error code, for the notice and the log.
   */
  onEnd(failure?: ListenFailure, detail?: string): void
}

export interface Listening {
  /** Stop listening and deliver whatever was heard. */
  stop(): void
  /** Stop listening and deliver nothing. */
  cancel(): void
}

/**
 * Start listening for one sentence.
 *
 * Not continuous, on purpose. Android's recogniser ends a continuous session
 * on the first pause anyway, and a front door wants one request, not a
 * transcript — the recogniser stopping when the customer does is the right
 * behaviour, not a limitation to work around.
 *
 * The language is the phone's, not the business's. A customer in a Marathi
 * salon may well be dictating in Marathi, and their phone already knows that.
 */
export function listen(handlers: ListenHandlers, lang = navigator.language): Listening | null {
  const Recognition = constructor()
  if (!Recognition) {
    voiceLog('listen: no recogniser on this browser')
    return null
  }

  const recognition = new Recognition()
  recognition.lang = lang
  recognition.continuous = false
  recognition.interimResults = true
  recognition.maxAlternatives = 1

  let finalText = ''
  /** Everything heard so far, committed or not. */
  let heardText = ''
  let failure: ListenFailure | undefined
  let detail: string | undefined
  let cancelled = false
  let ended = false

  recognition.onresult = (event) => {
    let committed = ''
    let pending = ''
    for (let index = 0; index < event.results.length; index += 1) {
      const result = event.results[index]
      const transcript = result?.[0]?.transcript ?? ''
      if (result?.isFinal) committed += transcript
      else pending += transcript
    }
    finalText = committed.trim()
    heardText = `${committed}${pending}`.trim()
    voiceLog(
      `result: ${event.results.length} result(s), final="${finalText}" heard="${heardText}"`,
    )
    handlers.onHeard(heardText)
  }

  recognition.onerror = (event) => {
    voiceLog(`error: ${event.error}${cancelled ? ' (after cancel)' : ''}`)
    detail = event.error
    switch (event.error) {
      case 'not-allowed':
      case 'service-not-allowed':
        failure = 'refused'
        break
      case 'no-speech':
        failure = 'silent'
        break
      case 'aborted':
        /*
         * Ours, via cancel() — or not. Android's recogniser also reports
         * `aborted` when something else takes the microphone from it, and
         * hiding that behind "nothing happened" is how a broken microphone
         * went unreported: the customer taps, speaks, and sees nothing.
         */
        if (!cancelled) failure = 'unavailable'
        break
      default:
        failure = 'unavailable'
    }
  }

  recognition.onend = () => {
    if (ended) return
    ended = true
    /*
     * What was heard, even if the recogniser never said it was sure.
     *
     * Stopped by a tap, Android's recogniser ends without marking its last
     * result final, so waiting for `isFinal` throws away a sentence the
     * customer watched appear word by word. The interim text is the best
     * sentence there is at that point, and it is theirs to edit in the box.
     */
    const sentence = finalText || heardText
    voiceLog(
      `end: ${cancelled ? 'cancelled' : (failure ?? 'ok')} sentence="${sentence}"`,
    )
    if (!cancelled && sentence) handlers.onFinal(sentence)
    handlers.onEnd(cancelled ? undefined : failure, cancelled ? undefined : detail)
  }

  try {
    recognition.start()
    voiceLog(`start: lang=${lang}`)
  } catch (error) {
    // Already started, or the API is present but inert. Either way there is
    // nothing to listen to.
    voiceLog(`start threw: ${error instanceof Error ? error.message : String(error)}`)
    return null
  }

  return {
    stop: () => {
      voiceLog('stop: asked')
      recognition.stop()
    },
    cancel: () => {
      voiceLog('cancel: asked')
      cancelled = true
      recognition.abort()
    },
  }
}
